<template>
  <div class="row">
    <div class="col-12">
      <div class="d-flex align-items-center justify-content-center day-switcher">
        <button class="btn btn-sm btn-outline-secondary date-nav-btn" @click="changeDay(-1)">
          <i class="material-icons">chevron_left</i>
        </button>
        <h6 class="mb-0 mx-3 date-display">{{ displayDate }}</h6>
        <button class="btn btn-sm btn-outline-secondary date-nav-btn" @click="changeDay(1)">
          <i class="material-icons">chevron_right</i>
        </button>
      </div>
      <card :title="$t('projects.projects')">
        <div class="row filter-row">
          <div class="col-12 col-md-4 mb-2">
            <el-input
              v-model="searchQuery"
              class="search-input"
              clearable
              :prefix-icon="Search"
              :placeholder="$t('common.searchRecords')"
              aria-controls="datatables"
              @keyup="search"
              @clear="handleClearSearch"
            />
          </div>
          <div class="col-6 col-md-4 mb-2">
            <el-date-picker
              v-model="apiDate"
              class="w-100"
              format="DD/MM/YYYY"
              value-format="YYYY-MM-DD"
              :clearable="false"
              @change="applyFilter"
            />
          </div>
          <div class="col-6 col-md-4 mb-2">
            <el-select
              v-model="statusFilter"
              :class="selectClass"
              :placeholder="$t('common.status')"
              class="w-100"
              @change="applyFilter"
            >
              <el-option
                v-for="status in statusOptions"
                :key="status.status"
                :label="status.status"
                :value="status.status"
              />
            </el-select>
          </div>
        </div>

        <div class="table-responsive">
          <el-table :data="projects" class="w-100" @row-click="goToProject">
            <el-table-column
              v-for="column in tableColumns"
              :key="column.label"
              :min-width="column.minWidth"
              :prop="column.prop"
              :label="column.label"
            />
            <el-table-column :min-width="135" align="right" label="">
              <template #default="{ row }">
                <!-- Standard mode (no assignment types) -->
                <template v-if="!enableProjectAssignmentTypes">
                  <material-button
                    v-if="row.status === $t('projectAssignments.notFinished')"
                    class="action-button"
                    variant="gradient"
                    color="danger"
                    size="sm"
                    @click.stop="handleMarkAsFinished(row)"
                  >
                    <i class="material-icons">rocket_launch</i>
                  </material-button>
                  <material-button
                    v-else
                    variant="outline"
                    color="dark"
                    size="sm"
                    @click.stop="handleShowRowInfo(row)"
                  >
                    <i class="material-icons">info</i>
                  </material-button>
                </template>
                <!-- Assignment Types mode -->
                <template v-else>
                  <!-- Same status colors as PreCreatedDayProjectAssignment: pink = not finished, default (success) = finished -->
                  <material-button
                    v-for="type in row.assignmentTypes"
                    :key="type.projectAssignmentTypeId"
                    :class="type.finished === $t('projectAssignments.notFinished') ? 'action-button me-1' : 'me-1'"
                    size="sm"
                    @click.stop="handleButtonClick(row, type)"
                  >
                    <span class="font-weight-bold">{{ type.type.charAt(0).toUpperCase() }}</span>
                  </material-button>
                </template>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center flex-wrap mt-3">
          <p class="text-sm text-secondary mb-0">
            {{ $t('pagination.showing') }} {{ from + 1 }} {{ $t('pagination.to') }} {{ to }} {{ $t('pagination.of') }} {{ total }} {{ $t('pagination.entries') }}
          </p>
          <material-pagination color="info" class="pagination-no-border mb-0">
            <material-pagination-item prev :disabled="pagination.currentPage === 1" @click="prevPage" />
            <material-pagination-item
              v-for="page in displayedPages"
              :key="page"
              :label="String(page)"
              :active="pagination.currentPage === page"
              @click="changePage(page)"
            />
            <material-pagination-item next :disabled="pagination.currentPage === totalPages" @click="nextPage" />
          </material-pagination>
        </div>
      </card>
    </div>
  </div>

  <!-- Comment Modal -->
  <modal v-model:show="showCommentModal">
    <div class="p-3">
      <div class="d-flex align-items-center mb-3">
        <div class="icon icon-shape bg-gradient-success shadow text-center border-radius-md me-3">
          <i class="material-icons opacity-10 pt-1">check_circle</i>
        </div>
        <div>
          <h5 class="mb-0">{{ $t('projectAssignments.setComment') }}</h5>
        </div>
      </div>
      <div class="mb-3">
        <material-input-field
          id="commentInput"
          v-model="comment"
          :label="$t('projectAssignments.comment')"
          :placeholder="$t('projectAssignments.comment')"
          name="comment"
          variant="static"
        />
      </div>
      <div class="d-flex justify-content-end">
        <material-button variant="gradient" color="dark" @click="createAssignment">
          <i class="material-icons me-1">check</i>
          {{ $t('common.confirm') }}
        </material-button>
      </div>
    </div>
  </modal>

  <!-- Project Info Modal -->
  <modal v-if="infoProject !== null" v-model:show="showProjectInfoModal">
    <div class="p-3">
      <div class="d-flex align-items-center mb-3">
        <div class="icon icon-shape bg-gradient-info shadow text-center border-radius-md me-3">
          <i class="material-icons opacity-10 pt-1">folder</i>
        </div>
        <div>
          <h5 class="mb-0">{{ infoProject.name }}{{ infoProject.type ? ' - ' + infoProject.type : '' }}</h5>
        </div>
      </div>
      <div class="ps-5 ms-3">
        <div v-if="isMobile && infoProject.user" class="mb-2">
          <i class="material-icons text-sm align-middle me-2 text-secondary">person</i>
          <span class="text-sm"><strong>{{ $t('projectAssignments.finishedBy') }}:</strong> {{ infoProject.user }}</span>
        </div>
        <div v-if="infoProject.status" class="mb-2">
          <i class="material-icons text-sm align-middle me-2 text-secondary">info</i>
          <span class="text-sm"><strong>{{ $t('common.status') }}:</strong> {{ infoProject.status }}</span>
        </div>
        <div v-if="infoProject.type" class="mb-2">
          <i class="material-icons text-sm align-middle me-2 text-secondary">category</i>
          <span class="text-sm"><strong>{{ $t('projectAssignments.type') }}:</strong> {{ infoProject.type }}</span>
        </div>
        <div v-if="infoProject.comment && userRole === 'User'" class="mb-2">
          <i class="material-icons text-sm align-middle me-2 text-secondary">chat</i>
          <span class="text-sm"><strong>{{ $t('projectAssignments.comment') }}:</strong> {{ infoProject.comment }}</span>
        </div>
        <div v-if="infoProject.price && userRole !== 'User'" class="mb-2">
          <i class="material-icons text-sm align-middle me-2 text-secondary">payments</i>
          <span class="text-sm text-success font-weight-bold"><strong>{{ $t('projects.price') }}:</strong> {{ infoProject.price }} Kr.</span>
        </div>
        <!-- Editable fields for admin -->
        <div v-if="userRole !== 'User'" class="mt-3">
          <div class="mb-3">
            <material-input-field
              id="updateCommentInput"
              v-model="updatedComment"
              :label="$t('projectAssignments.comment')"
              :placeholder="$t('projectAssignments.comment')"
              name="updatedComment"
              variant="static"
            />
          </div>
          <div class="mb-3">
            <material-input-field
              id="updateMultiplierInput"
              v-model="updatedMultiplier"
              :label="$t('projectAssignments.multiplier')"
              :placeholder="$t('projectAssignments.multiplier')"
              name="updatedMultiplier"
              variant="static"
            />
          </div>
        </div>
      </div>
      <!-- Save Button -->
      <div v-if="userRole !== 'User' && (updatedComment !== infoProject.comment || updatedMultiplier !== infoProject.multiplier)" class="mt-3">
        <material-button variant="gradient" color="success" full-width @click="updateAssignment">
          <i class="material-icons me-1">save</i>
          {{ $t('common.save') }}
        </material-button>
      </div>
      <!-- Unmark Button -->
      <div v-if="infoProject.userId === userId" class="mt-2">
        <material-button variant="gradient" color="danger" full-width @click="deleteAssignment">
          <i class="material-icons me-1">remove_circle</i>
          {{ $t('projectAssignments.unmark') }}
        </material-button>
      </div>
    </div>
  </modal>
</template>

<script>
import { Search } from '@element-plus/icons-vue';
import { shallowRef } from 'vue';
import { jwtDecode } from "jwt-decode";
import Cookies from 'js-cookie';
import api from "../../services/api";
import { getApiErrorMessage } from "../../utils/apiError";
import { ElInput, ElSelect, ElOption, ElTable, ElTableColumn } from 'element-plus';
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import swal from 'sweetalert2';
import Card from "../../components/Card.vue";
import Modal from "../../components/Modals/Modal.vue";
import MaterialButton from "../../components/MaterialButton.vue";
import MaterialInputField from "../../components/MaterialInputField.vue";
import MaterialPagination from "../../components/MaterialPagination.vue";
import MaterialPaginationItem from "../../components/MaterialPaginationItem.vue";

export default {
  components: {
    ElInput,
    ElSelect,
    ElOption,
    ElTable,
    ElTableColumn,
    Card,
    Modal,
    MaterialButton,
    MaterialInputField,
    MaterialPagination,
    MaterialPaginationItem,
  },
  data() {
    return {
      Search: shallowRef(Search),
      customerId: '',
      userId: '',
      userRole: '',
      updatedComment: '',
      updatedMultiplier: '',
      enableProjectAssignmentTypes: false,
      activeProject: null,
      infoProject: null,
      showCommentModal: false,
      showProjectInfoModal: false,
      apiDate: '',
      comment: '',
      pagination: { perPage: 10, currentPage: 1, total: 0 },
      searchQuery: '',
      projects: [],
      isMobile: window.innerWidth <= 1024,
      statusFilter: '',
    };
  },
  computed: {
    displayDate() {
      if (!this.apiDate) return '';
      const date = new Date(this.apiDate + 'T00:00:00');
      const weekday = this.$t(`dateNames.weekdays[${date.getDay()}]`);
      const day = date.getDate();
      const month = this.$t(`dateNames.months[${date.getMonth()}]`);
      const year = date.getFullYear();
      return `${weekday}, ${day}. ${month} ${year}`;
    },
    selectClass() {
      const darkMode = localStorage.getItem('darkMode');
      return darkMode === 'true' ? 'select-primary' : 'select-standard';
    },
    statusOptions() {
      return [
        { status: this.$t('projectAssignments.all') },
        { status: this.$t('projectAssignments.finished') },
        { status: this.$t('projectAssignments.notFinished') },
      ];
    },
    tableColumns() {
      if (this.isMobile) {
        return [{ prop: 'name', label: this.$t('common.name'), minWidth: 170 }];
      }
      return [
        { prop: 'key', label: this.$t('projects.key'), minWidth: 100 },
        { prop: 'name', label: this.$t('common.name'), minWidth: 200 },
        { prop: 'status', label: this.$t('common.status'), minWidth: 100 },
        { prop: 'user', label: this.$t('common.user'), minWidth: 200 },
      ];
    },
    // Pagination computed
    to() {
      let highBound = this.from + this.pagination.perPage;
      return this.total < highBound ? this.total : highBound;
    },
    from() { return this.pagination.perPage * (this.pagination.currentPage - 1); },
    total() { return this.pagination.total; },
    totalPages() { return this.total > 0 ? Math.ceil(this.total / this.pagination.perPage) : 1; },
    displayedPages() {
      const pages = [];
      const maxPages = Math.min(5, this.totalPages);
      let startPage = Math.max(1, this.pagination.currentPage - Math.floor(maxPages / 2));
      let endPage = startPage + maxPages - 1;
      if (endPage > this.totalPages) { endPage = this.totalPages; startPage = Math.max(1, endPage - maxPages + 1); }
      for (let i = startPage; i <= endPage; i++) pages.push(i);
      return pages;
    },
  },
  watch: {},
  created() {
    this.updateIsMobile();
    window.addEventListener('resize', this.updateIsMobile);

    const today = new Date();
    this.apiDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    if (this.isMobile) {
      this.statusFilter = this.$t('projectAssignments.all');
    }

    const savedSearchQuery = Cookies.get('todaysProjectsSearchQuery');
    if (savedSearchQuery) this.searchQuery = savedSearchQuery;

    const savedPage = Cookies.get('projectsPage');
    if (savedPage) this.pagination.currentPage = Number(savedPage);

    const token = Cookies.get('accessToken');
    const decodedToken = jwtDecode(token);
    this.userRole = decodedToken.role;
    this.customerId = decodedToken.CustomerId;
    this.userId = decodedToken.UserIdentifier;

    getCustomerConfig('DefaultPageSize').then(config => {
      this.pagination.perPage = config === '' ? 10 : config;
      getCustomerConfig('EnableProjectAssignmentTypes').then(conf => {
        this.enableProjectAssignmentTypes = conf !== '' ? conf : false;
        this.fetchData();
      });
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateIsMobile);
  },
  methods: {
    updateIsMobile() { this.isMobile = window.innerWidth <= 1024; },

    changeDay(offset) {
      const current = new Date(this.apiDate + 'T00:00:00');
      current.setDate(current.getDate() + offset);
      this.apiDate = this.formatDate(current);
      this.fetchData();
    },

    handleButtonClick(row, type) {
      if (type.finished === this.$t('projectAssignments.notFinished')) {
        this.handleMarkAsFinished(row, type);
      } else {
        this.handleShowRowInfo(row, type);
      }
    },

    goToProject(item) {
      this.$router.push({ name: 'Project', params: { projectId: item.id } });
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },

    isValidDecimal(value) {
      return /^-?\d+(\.\d+)?$/.test(String(value));
    },

    async updateAssignment() {
      if (this.updatedMultiplier !== '' && !this.isValidDecimal(this.updatedMultiplier)) {
        this.$toast.info(this.$t('projectAssignments.multiplierMustBeDecimal'), { timeout: 1500 });
        return;
      }
      try {
        await api.put(`/Project/Assignment/${this.infoProject.assignmentId}`, {
          projectId: this.infoProject.id,
          userId: this.infoProject.userId,
          toolId: null,
          quantity: null,
          startTime: this.infoProject.finishedAt,
          endTime: this.infoProject.finishedAt,
          active: false,
          externalUserEmail: null,
          comment: this.updatedComment,
          multiplier: this.updatedMultiplier === '' ? null : Number(this.updatedMultiplier),
        });
        swal.fire({
          title: this.$t('common.updated'),
          icon: 'success',
          customClass: { confirmButton: 'btn btn-success btn-fill' },
          buttonsStyling: false,
        });
        this.fetchData();
        this.showProjectInfoModal = false;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToUpdate') || 'Unable to update', { timeout: 0 });
      }
    },

    deleteAssignment() {
      const projectName = this.infoProject.name + (this.infoProject.type ? ` - ${this.infoProject.type}` : '');
      swal.fire({
        title: this.$t('projectAssignments.doYouWantToUnMark') + projectName + '?',
        icon: 'question',
        showCancelButton: true,
        customClass: { confirmButton: 'btn btn-success btn-fill', cancelButton: 'btn btn-danger btn-fill' },
        confirmButtonText: this.$t('common.yes'),
        cancelButtonText: this.$t('common.no'),
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          api.delete(`/Project/Assignment/${this.infoProject.assignmentId}`)
            .then(() => {
              this.$toast.success(this.$t('projectAssignments.unmarked'), { timeout: 1500 });
              this.showProjectInfoModal = false;
              this.infoProject = null;
              this.fetchData();
            })
            .catch(error => {
              console.error('API request error:', error); // eslint-disable-line no-console
              this.$toast.danger(this.$t('common.unableToDelete'), { timeout: 0 });
            });
        }
      });
    },

    async createAssignment() {
      try {
        const dateString = new Date(this.apiDate).toISOString();
        let body = {
          startTime: dateString,
          endTime: dateString,
          projectId: this.activeProject.id,
          comment: this.comment !== '' ? this.comment : null,
          preCreated: false,
          active: false,
          userId: this.userId,
        };
        if (this.enableProjectAssignmentTypes && this.activeProject.selectedType) {
          body.projectAssignmentTypeId = this.activeProject.selectedType.projectAssignmentTypeId;
        }
        await api.post('/Project/Assignment', body);
        this.fetchData();
        this.showCommentModal = false;
        this.activeProject = null;
        this.comment = '';
        this.$toast.success(this.$t('projectAssignments.assignmentMarkedAsFinished'), { timeout: 1500 });
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(getApiErrorMessage(this.$t, error), { timeout: 0 });
      }
    },

    handleMarkAsFinished(row, type = null) {
      const projectName = row.name + (type ? ` - ${type.type}` : '');
      swal.fire({
        title: this.$t('projectAssignments.doYouWantToMark') + projectName + this.$t('projectAssignments.asFinished'),
        icon: 'question',
        showCancelButton: true,
        customClass: { confirmButton: 'btn btn-success btn-fill', cancelButton: 'btn btn-danger btn-fill' },
        confirmButtonText: this.$t('common.yes'),
        cancelButtonText: this.$t('common.no'),
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.activeProject = row;
          if (type) this.activeProject.selectedType = type;
          this.showCommentModal = true;
        }
      });
    },

    handleShowRowInfo(row, type = null) {
      this.infoProject = { ...row };
      if (type) {
        Object.assign(this.infoProject, type);
        this.infoProject.status = type.finished;
        this.infoProject.user = type.finishedBy;
      }
      this.updatedComment = this.infoProject.comment;
      this.updatedMultiplier = this.infoProject.multiplier;
      this.showProjectInfoModal = true;
    },

    // Search & pagination
    search(event) {
      this.pagination.currentPage = 1;
      const queryLength = this.searchQuery.length;
      if (event.key === 'Backspace' || event.key === 'Enter') {
        this.fetchData();
      } else if (queryLength > 1 && queryLength % 2 === 0) {
        this.fetchData();
      }
    },

    handleClearSearch() {
      Cookies.remove('projectsPage');
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    applyFilter() {
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    changePage(page) {
      if (page !== this.pagination.currentPage && page >= 1 && page <= this.totalPages) {
        this.pagination.currentPage = page;
        Cookies.set('projectsPage', this.pagination.currentPage, { expires: new Date(new Date().getTime() + 60000) });
        this.fetchData();
      }
    },
    nextPage() { if (this.pagination.currentPage < this.totalPages) this.changePage(this.pagination.currentPage + 1); },
    prevPage() { if (this.pagination.currentPage > 1) this.changePage(this.pagination.currentPage - 1); },

    fetchData() {
      let projectUrl = '/Project/Today';
      if (this.enableProjectAssignmentTypes) {
        projectUrl = '/Project/Today/AssignmentTypes';
      }
      let baseParams = `?currentPage=${this.pagination.currentPage}&pageSize=${this.pagination.perPage}&projectStatusId=2&preCreatedAssignments=false&date=${this.apiDate}`;

      if (this.searchQuery === '') {
        Cookies.remove('todaysProjectsSearchQuery');
      } else {
        Cookies.set('todaysProjectsSearchQuery', this.searchQuery, { expires: new Date(new Date().getTime() + 60000) });
        baseParams += `&searchString=${encodeURIComponent(this.searchQuery)}`;
      }
      projectUrl += baseParams;

      // Server-side status filter
      if (this.statusFilter === this.$t('projectAssignments.finished')) {
        projectUrl += this.enableProjectAssignmentTypes ? '&statusFilter=finished' : '&assignmentsDoneToday=true';
      } else if (this.statusFilter === this.$t('projectAssignments.notFinished')) {
        projectUrl += this.enableProjectAssignmentTypes ? '&statusFilter=notFinished' : '&assignmentsDoneToday=false';
      }

      api.get(projectUrl)
        .then(response => {
          this.pagination.total = response.data.totalItems;

          if (!this.enableProjectAssignmentTypes) {
            this.projects = response.data.result.map(item => ({
              id: item.id,
              key: item.key,
              name: item.name,
              status: item.assignmentDoneToday ? this.$t('projectAssignments.finished') : this.$t('projectAssignments.notFinished'),
              user: item.assignmentDoneBy || '',
              finishedAt: item.assignmentDoneAt || '',
              comment: item.assignmentComment || '',
              assignmentId: item.todayProjectAssignmentId,
              userId: item.assignmentDoneByUserId,
              price: item.projectPrice,
              multiplier: item.assignmentMultiplier,
            }));
          } else {
            this.projects = response.data.result.map(item => {
              const assignmentTypesMapped = item.assignmentsTypes.map(type => ({
                projectAssignmentTypeId: type.projectAssignmentTypeId,
                type: type.type,
                finished: type.assignmentDoneToday ? this.$t('projectAssignments.finished') : this.$t('projectAssignments.notFinished'),
                finishedBy: type.assignmentDoneBy || '',
                finishedAt: type.assignmentDoneAt || '',
                comment: type.assignmentComment || '',
                assignmentId: type.todayProjectAssignmentId,
                userId: type.assignmentDoneByUserId,
                multiplier: type.multiplier,
              }));
              const uniqueStatuses = [...new Set(assignmentTypesMapped.map(t => t.finished))];
              const finishedBys = assignmentTypesMapped.filter(t => t.finishedBy).map(t => t.finishedBy).join(', ');
              return {
                id: item.id,
                key: item.key,
                name: item.name,
                status: uniqueStatuses.join(', '),
                user: finishedBys,
                price: item.projectPrice,
                assignmentTypes: assignmentTypesMapped,
              };
            });
          }
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
        });
    },
  },
};
</script>

<style scoped>
.action-button {
  background-color: #fd5d93 !important;
}

.icon-shape {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
}

.icon-shape i {
  font-size: 1.25rem;
  color: #fff;
}

.date-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 0.5rem;
}

.date-nav-btn .material-icons {
  font-size: 1.25rem;
}

.day-switcher {
  /* same distance from the navbar as the cards (Card.vue .card-margin) */
  margin-top: 20px;
}

/* Element Plus gives the date editor a fixed width; let it fill its filter column */
.filter-row :deep(.el-date-editor) {
  width: 100%;
}

.date-display {
  font-weight: 500;
  white-space: nowrap;
}

:deep(.el-table) .material-icons {
  font-size: 1.1rem;
  vertical-align: middle;
  line-height: 1;
}

:deep(.el-table) .btn .material-icons {
  font-size: 1.1rem;
}
</style>
